import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { useAuth } from '../contexts/AuthContext';
import { BookOpen, ArrowLeft, Upload } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function CreateCourse() {
  const navigate = useNavigate();
  const { getAuthHeader } = useAuth();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    subject: '',
    duration: '',
    price: '',
    thumbnail: ''
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await axios.post(
        `${API}/courses`,
        { ...formData, price: parseFloat(formData.price) },
        getAuthHeader()
      );
      toast.success('Course created successfully!');
      navigate('/teacher/dashboard');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create course');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-white border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center h-16">
            <Button
              data-testid="back-btn"
              variant="ghost"
              size="sm"
              onClick={() => navigate('/teacher/dashboard')}
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Dashboard
            </Button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-8">
          <h1 className="text-3xl md:text-4xl font-extrabold text-slate-900 mb-2">Create New Course</h1>
          <p className="text-lg text-slate-600">Fill in the details to create a new course</p>
        </div>

        <Card className="bg-white rounded-xl border border-slate-100 shadow-sm p-8">
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Course Title */}
            <div>
              <Label htmlFor="title" className="text-slate-700 font-medium text-base mb-2 block">
                Course Title *
              </Label>
              <Input
                id="title"
                data-testid="course-title-input"
                type="text"
                placeholder="e.g., Advanced Mathematics for Grade 12"
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                className="h-12 rounded-lg border-slate-200 focus:border-indigo-500 focus:ring-indigo-500/20 bg-slate-50 focus:bg-white transition-all text-slate-900"
                required
              />
            </div>

            {/* Course Description */}
            <div>
              <Label htmlFor="description" className="text-slate-700 font-medium text-base mb-2 block">
                Description *
              </Label>
              <Textarea
                id="description"
                data-testid="course-description-input"
                placeholder="Describe what students will learn in this course..."
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                className="min-h-[120px] rounded-lg border-slate-200 focus:border-indigo-500 focus:ring-indigo-500/20 bg-slate-50 focus:bg-white transition-all text-slate-900"
                required
              />
            </div>

            {/* Subject and Duration */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <Label htmlFor="subject" className="text-slate-700 font-medium text-base mb-2 block">
                  Subject *
                </Label>
                <Input
                  id="subject"
                  data-testid="course-subject-input"
                  type="text"
                  placeholder="e.g., Mathematics, Physics, Chemistry"
                  value={formData.subject}
                  onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
                  className="h-12 rounded-lg border-slate-200 focus:border-indigo-500 focus:ring-indigo-500/20 bg-slate-50 focus:bg-white transition-all text-slate-900"
                  required
                />
              </div>

              <div>
                <Label htmlFor="duration" className="text-slate-700 font-medium text-base mb-2 block">
                  Duration *
                </Label>
                <Input
                  id="duration"
                  data-testid="course-duration-input"
                  type="text"
                  placeholder="e.g., 3 months, 12 weeks"
                  value={formData.duration}
                  onChange={(e) => setFormData({ ...formData, duration: e.target.value })}
                  className="h-12 rounded-lg border-slate-200 focus:border-indigo-500 focus:ring-indigo-500/20 bg-slate-50 focus:bg-white transition-all text-slate-900"
                  required
                />
              </div>
            </div>

            {/* Price */}
            <div>
              <Label htmlFor="price" className="text-slate-700 font-medium text-base mb-2 block">
                Price (INR) *
              </Label>
              <div className="relative">
                <span className="absolute left-4 top-1/2 transform -translate-y-1/2 text-slate-600 font-semibold">₹</span>
                <Input
                  id="price"
                  data-testid="course-price-input"
                  type="number"
                  step="0.01"
                  min="0"
                  placeholder="999.00"
                  value={formData.price}
                  onChange={(e) => setFormData({ ...formData, price: e.target.value })}
                  className="pl-12 h-12 rounded-lg border-slate-200 focus:border-indigo-500 focus:ring-indigo-500/20 bg-slate-50 focus:bg-white transition-all text-slate-900"
                  required
                />
              </div>
            </div>

            {/* Thumbnail URL */}
            <div>
              <Label htmlFor="thumbnail" className="text-slate-700 font-medium text-base mb-2 block">
                Thumbnail Image URL
              </Label>
              <div className="relative">
                <Upload className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-slate-400" />
                <Input
                  id="thumbnail"
                  data-testid="course-thumbnail-input"
                  type="url"
                  placeholder="https://example.com/image.jpg (optional)"
                  value={formData.thumbnail}
                  onChange={(e) => setFormData({ ...formData, thumbnail: e.target.value })}
                  className="pl-12 h-12 rounded-lg border-slate-200 focus:border-indigo-500 focus:ring-indigo-500/20 bg-slate-50 focus:bg-white transition-all text-slate-900"
                />
              </div>
              <p className="text-sm text-slate-500 mt-2">Leave empty to use default thumbnail</p>
            </div>

            {/* Action Buttons */}
            <div className="flex items-center justify-end space-x-4 pt-6 border-t border-slate-200">
              <Button
                type="button"
                variant="outline"
                onClick={() => navigate('/teacher/dashboard')}
                className="rounded-full px-8"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                data-testid="submit-course-btn"
                disabled={loading}
                className="rounded-full bg-indigo-600 hover:bg-indigo-700 text-white px-8 shadow-lg shadow-indigo-500/20 hover:-translate-y-0.5 transition-all duration-200"
              >
                {loading ? 'Creating...' : 'Create Course'}
              </Button>
            </div>
          </form>
        </Card>
      </main>
    </div>
  );
}
